/**
 * Constants the analysis modules read, copied from the previous dashboard's
 * constants.js (CONSTANTS.STATUS_LABELS, PREDICTION, INVENTORY,
 * COMPONENT_LIFETIMES and the affectedSensors lists of FAILURE_SCENARIOS).
 * Values are verbatim; see PORT_NOTES.md.
 */
import type { StatusColour } from '../types';

/** Card labels per status colour (CONSTANTS.STATUS_LABELS). */
export const STATUS_LABELS: Record<StatusColour, string> = {
  green: 'OPERATIONAL',
  yellow: 'DEGRADED',
  red: 'CRITICAL',
};

/** Prediction configuration (CONSTANTS.PREDICTION). */
export const PREDICTION = {
  /** Years of synthetic history to generate. */
  HISTORICAL_YEARS: 5,
  /** Hourly readings. */
  READINGS_PER_DAY: 24,
  /** Two years ahead. */
  PREDICTION_HORIZON_DAYS: 730,
  /** Minimum confidence to report (fraction). */
  CONFIDENCE_THRESHOLD: 0.7,
  /** Data window for regression, in days. */
  REGRESSION_WINDOW_DAYS: 365,
  /** Days before failure to warn. */
  WARNING_LEAD_DAYS: 180,
  /** Days before failure for a critical alert. */
  CRITICAL_LEAD_DAYS: 30,
} as const;

/** Inventory configuration (CONSTANTS.INVENTORY). */
export const INVENTORY = {
  /** Quantity for the low stock warning. */
  LOW_STOCK_THRESHOLD: 2,
  /** Quantity for the critical warning. */
  CRITICAL_STOCK_THRESHOLD: 1,
  /** Auto-reserve parts on critical alerts. */
  AUTO_RESERVE_ON_ALERT: true,
  /** Out-of-range readings before an inventory check. */
  CONSECUTIVE_OOR_TRIGGER: 3,
  STATUS_COLORS: {
    IN_STOCK: '#4CAF50',
    LOW_STOCK: '#FF9800',
    OUT_OF_STOCK: '#F44336',
    ON_ORDER: '#2196F3',
  } as Record<string, string>,
} as const;

export interface ComponentLifetime {
  /** Years. */
  baseLifetime: number;
  mtbfHours: number;
  category: string;
}

/** Industry-standard MTBF values by component id keyword (CONSTANTS.COMPONENT_LIFETIMES). Key order matters: the first keyword found in an id wins. */
export const COMPONENT_LIFETIMES: Record<string, ComponentLifetime> = {
  // Moving parts degrade faster
  cooling_fan: { baseLifetime: 3, mtbfHours: 30000, category: 'Mechanical' },
  fan: { baseLifetime: 3, mtbfHours: 30000, category: 'Mechanical' },
  drive_system: { baseLifetime: 8, mtbfHours: 70000, category: 'Mechanical' },
  antenna: { baseLifetime: 15, mtbfHours: 130000, category: 'Mechanical' },
  motor: { baseLifetime: 10, mtbfHours: 90000, category: 'Mechanical' },

  // Electronics with thermal stress
  hpa: { baseLifetime: 7, mtbfHours: 60000, category: 'RF Electronics' },
  sspa: { baseLifetime: 8, mtbfHours: 70000, category: 'RF Electronics' },
  lna: { baseLifetime: 10, mtbfHours: 90000, category: 'RF Electronics' },
  low_noise_amplifier: { baseLifetime: 10, mtbfHours: 90000, category: 'RF Electronics' },
  freq_translator: { baseLifetime: 12, mtbfHours: 100000, category: 'RF Electronics' },
  frequency_translator: { baseLifetime: 12, mtbfHours: 100000, category: 'RF Electronics' },

  // Digital electronics (longer life)
  modem: { baseLifetime: 10, mtbfHours: 90000, category: 'Digital' },
  network_switch: { baseLifetime: 10, mtbfHours: 90000, category: 'Digital' },
  switch_matrix: { baseLifetime: 15, mtbfHours: 130000, category: 'Digital' },
  ifl_switch: { baseLifetime: 15, mtbfHours: 130000, category: 'Digital' },
  data_processor: { baseLifetime: 8, mtbfHours: 70000, category: 'Digital' },
  spectrum_analyzer: { baseLifetime: 12, mtbfHours: 100000, category: 'Digital' },

  // Power systems
  power_supply: { baseLifetime: 7, mtbfHours: 60000, category: 'Power' },
  ups: { baseLifetime: 5, mtbfHours: 45000, category: 'Power' },

  // Passive components (longest life)
  waveguide: { baseLifetime: 25, mtbfHours: 220000, category: 'Passive' },
  feed_horn: { baseLifetime: 20, mtbfHours: 175000, category: 'Passive' },
  reflector: { baseLifetime: 25, mtbfHours: 220000, category: 'Passive' },
  backup_structure: { baseLifetime: 30, mtbfHours: 260000, category: 'Structural' },

  // Default for unknown components
  default: { baseLifetime: 10, mtbfHours: 90000, category: 'Other' },
};

/** affectedSensors per failure scenario (CONSTANTS.FAILURE_SCENARIOS); the inventory out-of-range counter only runs for these. */
export const FAILURE_SCENARIO_AFFECTED_SENSORS: Record<string, readonly string[]> = {
  FAN_FAILURE: ['fan_speed', 'fan_temperature', 'lna_temperature', 'hpa_temperature'],
  LNA_DEGRADATION: ['lna_noise_figure', 'lna_temperature', 'lna_gain'],
  HPA_OVERTEMP: ['hpa_temperature', 'hpa_output_power', 'hpa_efficiency'],
  MOTOR_BEARING_WEAR: ['antenna_az_current', 'antenna_el_current', 'antenna_vibration'],
  MODEM_UNLOCK: ['modem_lock', 'modem_ber', 'modem_eb_n0'],
};
